import { useCallback } from 'react'

import { Ok, Result } from 'ts-results'

import { DataError, useApi } from 'app/common/application'

import {
  InviteUserGetRequest,
  InviteUserGetResponse,
  InviteUserRequest,
  InviteUserResponse,
  InviteUserStatus,
  WorkspaceCreateContent,
  WorkspaceCreateRequest,
  WorkspaceCreateResponse,
  WorkspaceUser,
  WorkspaceUsersGetRequest,
  WorkspaceUsersGetResponse,
} from '../types'

const useWorkspace = () => {
  const { create } = useApi()

  const createWorkspace = useCallback(
    (content: WorkspaceCreateContent): Promise<Result<string, DataError<number>>> =>
      create<WorkspaceCreateRequest, WorkspaceCreateResponse>('/core/user/create', {
        content,
      }).then((result) => (result.ok ? Ok(result.val.data.selectedWorkspaceId) : result)),
    [create],
  )

  const getWorkspaceUsers = useCallback(
    (): Promise<Result<WorkspaceUser[], DataError<number>>> =>
      create<WorkspaceUsersGetRequest, WorkspaceUsersGetResponse>(
        '/core/workspaces/getWorkspaceUsers',
        {},
      ).then((result) => (result.ok ? Ok(result.val.data) : result)),
    [create],
  )

  const getInvite = useCallback(
    (
      email: string,
      inviteCode: string,
      workspace: boolean,
    ): Promise<Result<InviteUserStatus, DataError<number>>> =>
      create<InviteUserGetRequest, InviteUserGetResponse>('/core/user/getInvite', {
        content: { email, inviteCode, workspace },
      }).then((result) => (result.ok ? Ok(result.val.data) : result)),
    [create],
  )

  const inviteUser = useCallback(
    (email: string): Promise<Result<void, DataError<number>>> =>
      create<InviteUserRequest, InviteUserResponse>('/core/user/inviteUser', {
        content: { email },
      }).then((result) => (result.ok ? Ok(undefined) : result)),
    [create],
  )

  return {
    createWorkspace,
    getWorkspaceUsers,
    getInvite,
    inviteUser,
  }
}

export default useWorkspace
